import React, { useState, useRef, useMemo } from "react";
import { Checkbox } from "@/components/ui/checkbox";

// Section group for grouping columns under a section header
export interface SectionGroup {
  sectionId: string;
  sectionTitle: string;
  startColumnIndex: number; // Index in data array (0-based, excludes checkbox)
  columnCount: number;
}

interface MT_ColumnHeaderProps {
  data: { key: string; label?: string; fixedX: boolean; sectionId?: string; sectionTitle?: string }[];
  columnWidths: number[];
  onColumnResize: (index: number, newWidth: number) => void;
  allSelected: boolean;
  onSelectAll: () => void;
}
const COLUMN_HEIGHT = 40; // Reduced for field names
const SECTION_HEADER_HEIGHT = 28; // Height for section headers
const MIN_COLUMN_WIDTH = 75; // Minimum width for data columns
const MIN_CHECKBOX_WIDTH = 50; // Minimum width for checkbox column

const MT_ColumnHeader: React.FC<MT_ColumnHeaderProps> = ({
  data,
  columnWidths,
  onColumnResize,
  allSelected,
  onSelectAll,
}) => {
  const [resizingIndex, setResizingIndex] = useState<number | null>(null);
  const startXRef = useRef<number>(0);
  const startWidthRef = useRef<number>(0);

  // #region Resize
  const handleMouseDown = (e: React.MouseEvent, index: number) => {
    e.preventDefault();

    setResizingIndex(index);
    startXRef.current = e.clientX;
    startWidthRef.current = columnWidths[index];

    const handleMouseMove = (moveEvent: MouseEvent) => {
      const delta = moveEvent.clientX - startXRef.current;
      const newWidth = startWidthRef.current + delta;
      // First column (checkbox) has 50px minimum, others have 75px minimum
      const minWidth = index === 0 ? MIN_CHECKBOX_WIDTH : MIN_COLUMN_WIDTH;
      onColumnResize(index, Math.max(minWidth, newWidth));
    };

    const handleMouseUp = () => {
      setResizingIndex(null);
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };

    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
  };
  // #endregion
  
  // Default width fallback for columns not yet in columnWidths array
  const DEFAULT_COLUMN_WIDTH = 150;
  
  // Calculate cumulative left positions for sticky columns
  const getStickyLeft = (columnIndex: number): number => {
    let left = 0;
    for (let i = 0; i < columnIndex; i++) {
      left += columnWidths[i] ?? DEFAULT_COLUMN_WIDTH;
    }
    return left;
  };

  // Build section groups from column data (including metadata group)
  const sectionGroups = useMemo((): SectionGroup[] => {
    const groups: SectionGroup[] = [];
    let currentSection: SectionGroup | null = null;
    let metadataGroup: SectionGroup | null = null;

    data.forEach((column, index) => {
      if (column.sectionId && column.sectionTitle) {
        // Close metadata group if we were tracking one
        if (metadataGroup) {
          groups.push(metadataGroup);
          metadataGroup = null;
        }
        
        if (currentSection && currentSection.sectionId === column.sectionId) {
          // Continue current section
          currentSection.columnCount++;
        } else {
          // Start new section
          if (currentSection) {
            groups.push(currentSection);
          }
          currentSection = {
            sectionId: column.sectionId,
            sectionTitle: column.sectionTitle,
            startColumnIndex: index,
            columnCount: 1,
          };
        }
      } else {
        // Non-section column (metadata), close current section if any
        if (currentSection) {
          groups.push(currentSection);
          currentSection = null;
        }
        
        // Track metadata columns as a group
        if (metadataGroup) {
          metadataGroup.columnCount++;
        } else {
          metadataGroup = {
            sectionId: '_metadata',
            sectionTitle: 'Metadata',
            startColumnIndex: index,
            columnCount: 1,
          };
        }
      }
    });

    // Push remaining groups
    if (metadataGroup) {
      groups.push(metadataGroup);
    }
    if (currentSection) {
      groups.push(currentSection);
    }

    // Sort groups by startColumnIndex to ensure correct order
    groups.sort((a, b) => a.startColumnIndex - b.startColumnIndex);

    return groups;
  }, [data]);

  // Check if we have any field columns (from sections) - only show header row when we have field columns
  const hasFieldColumns = data.some(col => col.sectionId && col.sectionTitle);
  const hasSectionHeaders = hasFieldColumns;

  // Calculate left position for a section header
  const getSectionLeft = (startColumnIndex: number): number => {
    // Start after checkbox column
    let left = columnWidths[0] ?? MIN_CHECKBOX_WIDTH;
    for (let i = 0; i < startColumnIndex; i++) {
      left += columnWidths[i + 1] ?? DEFAULT_COLUMN_WIDTH;
    }
    return left;
  };

  // Calculate width for a section header
  const getSectionWidth = (startColumnIndex: number, columnCount: number): number => {
    let width = 0;
    for (let i = 0; i < columnCount; i++) {
      width += columnWidths[startColumnIndex + i + 1] ?? DEFAULT_COLUMN_WIDTH; // +1 for checkbox column offset
    }
    return width;
  };

  return (
    <div className="sticky top-0 z-30">
      {/* Section headers row - only if we have sections */}
      {hasSectionHeaders && (
        <div className="flex items-center bg-background flex-shrink-0 relative" style={{ height: SECTION_HEADER_HEIGHT }}>
          {/* Empty space for checkbox column */}
          <div
            className="sticky left-0 z-30 bg-background border-b border-border"
            style={{
              width: columnWidths[0] ?? MIN_CHECKBOX_WIDTH,
              minWidth: columnWidths[0] ?? MIN_CHECKBOX_WIDTH,
              height: SECTION_HEADER_HEIGHT,
            }}
          />
          
          {/* Section headers positioned absolutely to span multiple columns */}
          {sectionGroups.map((section) => (
            <div
              key={section.sectionId}
              className="absolute flex items-center justify-center border border-border bg-muted text-xs font-medium text-muted-foreground"
              style={{
                left: getSectionLeft(section.startColumnIndex),
                width: getSectionWidth(section.startColumnIndex, section.columnCount),
                height: SECTION_HEADER_HEIGHT,
              }}
            >
              <span className="truncate px-2">{section.sectionTitle}</span>
            </div>
          ))}
          
          {/* Fill remaining space for non-section columns */}
          <div className="flex-1 border-b border-border" />
        </div>
      )}

      {/* Column headers row */}
      <div className="flex items-center bg-background flex-shrink-0 relative">
        {/* Checkbox column - always sticky */}
        <div
          className="flex items-center justify-center border border-border p-2 overflow-hidden sticky left-0 z-30 bg-background"
          style={{
            width: columnWidths[0] ?? MIN_CHECKBOX_WIDTH,
            minWidth: columnWidths[0] ?? MIN_CHECKBOX_WIDTH,
            maxWidth: columnWidths[0] ?? MIN_CHECKBOX_WIDTH,
            height: COLUMN_HEIGHT,
            left: 0,
          }}
        >
          <Checkbox checked={allSelected} onCheckedChange={onSelectAll} />
        </div>

        {/* Data columns */}
        {data.map((column, index) => {
          const isSticky = column.fixedX;
          const stickyLeft = isSticky ? getStickyLeft(index + 1) : undefined;
          // Create unique key using sectionId + field key to avoid duplicates across sections
          const uniqueColumnKey = column.sectionId ? `${column.sectionId}_${column.key}` : column.key;
          
          return (
            <div
              key={uniqueColumnKey}
              className={`relative flex items-center justify-center border border-border p-2 overflow-hidden bg-background 
              } ${isSticky ? "sticky z-30 bg-background" : ""}`}
              style={{
                width: columnWidths[index + 1] ?? DEFAULT_COLUMN_WIDTH, // +1 because checkbox is at index 0
                minWidth: columnWidths[index + 1] ?? DEFAULT_COLUMN_WIDTH,
                maxWidth: columnWidths[index + 1] ?? DEFAULT_COLUMN_WIDTH,
                height: COLUMN_HEIGHT,
                ...(isSticky && { left: stickyLeft }),
              }}
            >
              <span className="truncate px-1 w-full text-left text-sm">
                {column.label || column.key}
              </span>
              <div
                className={`absolute right-0 top-0 bottom-0 w-1 cursor-col-resize  transition-colors ${
                  resizingIndex === index ? "bg-primary" : "bg-transparent"
                }`}
                style={{ width: "4px" }}
                onMouseDown={(e) => handleMouseDown(e, index + 1)} // +1 to account for checkbox column
              />
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default MT_ColumnHeader;
